import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import App from './App'
import { WagmiProvider } from 'wagmi'
import { wagmiConfig } from './config/wagmi'
import NotificationProvider from './components/NotificationProvider'
import WalletProvider from './components/WalletProvider'
import { SafeWalletProvider } from './localsafe.eth/provider/SafeWalletProvider'
import { ThemeProvider, useTheme } from './contexts/ThemeContext'
import { TreasuryBalanceProvider } from './contexts/TreasuryBalanceContext'
import './index.css'

export function MuiThemeWrapper({ children }: { children: React.ReactNode }) {
  const { colors, fontFamily } = useTheme()

  const muiTheme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'light',
          primary: {
            main: colors.primary,
          },
          secondary: {
            main: colors.secondary,
          },
          background: {
            default: colors.background,
          },
          text: {
            primary: colors.text,
          },
        },
        typography: {
          fontFamily: fontFamily,
          h1: {
            fontFamily: fontFamily,
            fontWeight: 700,
          },
          h2: {
            fontFamily: fontFamily,
            fontWeight: 700,
          },
          h3: {
            fontFamily: fontFamily,
            fontWeight: 600,
          },
          h4: {
            fontFamily: fontFamily,
            fontWeight: 600,
          },
          h5: {
            fontFamily: fontFamily,
            fontWeight: 500,
          },
          h6: {
            fontFamily: fontFamily,
            fontWeight: 500,
          },
          body1: {
            fontFamily: fontFamily,
            fontWeight: 400,
          },
          body2: {
            fontFamily: fontFamily,
            fontWeight: 400,
          },
          button: {
            fontFamily: fontFamily,
            fontWeight: 500,
            textTransform: 'uppercase',
          },
        },
      }),
    [colors, fontFamily]
  )

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  )
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <MuiThemeWrapper>
          <NotificationProvider>
            <WagmiProvider config={wagmiConfig}>
              <SafeWalletProvider>
                <WalletProvider>
                  <TreasuryBalanceProvider>
                    <App />
                  </TreasuryBalanceProvider>
                </WalletProvider>
              </SafeWalletProvider>
            </WagmiProvider>
          </NotificationProvider>
        </MuiThemeWrapper>
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>
)
